/**
 * Admin Settings View
 *
 * A comprehensive admin settings page with tabbed interface for:
 * - General Settings (approval thresholds)
 * - Currencies management
 * - Executive Approvers configuration (CFO/CEO)
 * - Audit Log viewer
 *
 * Access is restricted to users with ADMIN role.
 */

import { apiClient } from '../services/api-client';
import { getAuthState, selectIsAdmin } from '../services/auth-store';
import { renderIcon } from '../utils/icons';
import { createSidebarLayout } from '../components/sidebar-layout';
import {
  renderQuickBooksConnectionStatus,
  renderQuickBooksLoading,
  attachQuickBooksConnectionListeners,
  type QuickBooksConnectionStatus,
} from '../components/quickbooks-connection-status';
import { confirm as confirmModal } from '../components/modal';
import { toastSuccess, toastError } from '../components/toast';

// ============================================================================
// Types
// ============================================================================

type TabId = 'general' | 'currencies' | 'executive-approvers' | 'quickbooks' | 'audit-log';

interface ApprovalThresholds {
  deptHead: number;
  cfo: number;
  ceo: number;
}

interface Currency {
  code: string;
  name: string;
  symbol: string;
  isActive: boolean;
  isDefault: boolean;
  createdAt: string;
}

interface ExecutiveApprover {
  id: string;
  role: 'CFO' | 'CEO';
  userId: string;
  user?: {
    id: string;
    displayName: string;
    email: string;
  };
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface ExecutiveApproversConfig {
  cfo: ExecutiveApprover | null;
  ceo: ExecutiveApprover | null;
}

interface AuditLogEntry {
  id: string;
  request_id: string | null;
  user_id: string;
  action: string;
  details: string;
  ip_address: string | null;
  user_agent: string | null;
  created_at: string;
}

interface AuditLogPagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
  hasMore: boolean;
}

interface User {
  id: string;
  displayName: string;
  email: string;
}

// ============================================================================
// Admin Settings View Class
// ============================================================================

export class AdminSettingsView {
  private container: HTMLElement;
  private activeTab: TabId = 'general';
  private errorMessage: string | null = null;
  private successMessage: string | null = null;

  // Data state
  private approvalThresholds: ApprovalThresholds | null = null;
  private currencies: Currency[] = [];
  private executiveApprovers: ExecutiveApproversConfig | null = null;
  private auditLogEntries: AuditLogEntry[] = [];
  private auditLogPagination: AuditLogPagination = {
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
    hasMore: false,
  };
  private auditLogFilter: {
    action?: string;
    userId?: string;
    requestId?: string;
    fromDate?: string;
    toDate?: string;
  } = {};
  private auditLogSearchQuery = '';
  private auditLogExpandedRows: Set<string> = new Set();
  private userNameCache: Map<string, string> = new Map();

  // User search state for executive approvers
  private userSearchResults: User[] = [];

  // QuickBooks connection state
  private quickBooksStatus: QuickBooksConnectionStatus | null = null;
  private quickBooksLoading = false;
  private quickBooksLoadError: string | null = null;

  constructor(container: HTMLElement) {
    this.container = container;
  }

  /**
   * Initialize and render the admin settings view
   */
  public async init(): Promise<void> {
    // Check admin access
    const authState = getAuthState();
    if (!selectIsAdmin(authState)) {
      this.renderAccessDenied();
      return;
    }

    // Check for QuickBooks OAuth callback parameters
    this.handleQuickBooksCallbackParams();

    this.renderLoading();
    await this.loadInitialData();
    this.render();
  }

  /**
   * Handle QuickBooks OAuth callback redirect parameters
   */
  private handleQuickBooksCallbackParams(): void {
    const urlParams = new URLSearchParams(window.location.search);
    const qbConnected = urlParams.get('qb_connected');
    const qbError = urlParams.get('qb_error');

    if (qbConnected === 'true') {
      this.successMessage = 'Successfully connected to QuickBooks!';
      this.activeTab = 'quickbooks';
      // Load QuickBooks status immediately
      this.loadQuickBooksStatus();
      // Clean up URL
      this.cleanUrlParams(['qb_connected']);
    } else if (qbError) {
      const errorMessages: Record<string, string> = {
        'missing_code': 'Authorization was cancelled or failed. Please try again.',
        'missing_realm_id': 'Company information was not received. Please try again.',
        'invalid_state': 'Security validation failed. Please try connecting again.',
        'token_exchange_failed': 'Failed to complete authorization. Please try again.',
        'access_denied': 'Access was denied by QuickBooks. Please authorize the connection.',
      };
      this.errorMessage = errorMessages[qbError] || `QuickBooks connection failed: ${qbError}`;
      this.activeTab = 'quickbooks';
      // Clean up URL
      this.cleanUrlParams(['qb_error']);
    }
  }

  /**
   * Remove specified parameters from URL without page reload
   */
  private cleanUrlParams(paramsToRemove: string[]): void {
    const url = new URL(window.location.href);
    paramsToRemove.forEach(param => url.searchParams.delete(param));
    window.history.replaceState({}, '', url.toString());
  }

  private async loadInitialData(): Promise<void> {
    this.errorMessage = null;

    try {
      // Load all data in parallel
      const [settingsResponse, currenciesResponse, approversResponse] = await Promise.all([
        apiClient.get<{ data: { approvalThresholds: ApprovalThresholds } }>('/admin/settings'),
        apiClient.get<{ data: Currency[] }>('/admin/currencies'),
        apiClient.get<{ data: ExecutiveApproversConfig }>('/admin/executive-approvers'),
      ]);

      this.approvalThresholds = settingsResponse.data.data.approvalThresholds;
      this.currencies = currenciesResponse.data.data;
      this.executiveApprovers = approversResponse.data.data;
    } catch (error) {
      this.errorMessage = (error as { message?: string }).message || 'Failed to load settings';
    }
  }

  private renderLoading(): void {
    this.container.innerHTML = `
      <div class="min-h-screen flex items-center justify-center p-4">
        <div class="glass-card w-full max-w-md p-8 rounded-2xl text-center">
          <div class="flex justify-center mb-4">
            ${renderIcon('loader', { size: 'xl', className: 'animate-spin text-primary' }).outerHTML}
          </div>
          <p class="text-muted-foreground">Loading admin settings...</p>
        </div>
      </div>
    `;
  }

  private renderAccessDenied(): void {
    this.container.innerHTML = `
      <div class="min-h-screen flex items-center justify-center p-4">
        <div class="glass-card w-full max-w-md p-8 rounded-2xl text-center">
          <div class="flex justify-center mb-4">
            <div class="w-16 h-16 rounded-full bg-destructive/20 flex items-center justify-center">
              ${renderIcon('shield', { size: 'xl', className: 'text-destructive' }).outerHTML}
            </div>
          </div>
          <h1 class="text-2xl font-bold text-foreground mb-2">Access Denied</h1>
          <p class="text-muted-foreground mb-6">You do not have permission to access admin settings. Only administrators can view this page.</p>
          <a href="/" class="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground font-medium rounded-lg hover:bg-primary/90 transition-colors">
            ${renderIcon('home', { size: 'sm' }).outerHTML}
            <span>Go to Home</span>
          </a>
        </div>
      </div>
    `;
  }

  private render(): void {
    if (this.errorMessage && !this.approvalThresholds) {
      this.renderError();
      return;
    }

    this.container.innerHTML = `
      <div class="px-4 lg:px-6 py-6">
        <div class="max-w-6xl mx-auto">
          <!-- Subtitle -->
          <p class="text-muted-foreground mb-6">Manage system configuration, currencies, approvers, and view audit logs</p>

          <!-- Alert Messages -->
          <div id="alert-container" class="mb-6"></div>

          <!-- Tabbed Interface -->
          <div class="glass-card rounded-2xl overflow-hidden">
            <!-- Tab Navigation -->
            <nav class="flex border-b border-border overflow-x-auto" role="tablist">
              ${this.renderTabButton('general', 'General Settings', 'settings')}
              ${this.renderTabButton('currencies', 'Currencies', 'banknote')}
              ${this.renderTabButton('executive-approvers', 'Executive Approvers', 'users')}
              ${this.renderTabButton('quickbooks', 'QuickBooks', 'creditCard')}
              ${this.renderTabButton('audit-log', 'Audit Log', 'fileText')}
            </nav>

            <!-- Tab Content -->
            <div class="p-6" id="tab-content">
              ${this.renderActiveTabContent()}
            </div>
          </div>
        </div>
      </div>
    `;

    this.renderAlerts();
    this.attachEventListeners();
  }

  private renderTabButton(tabId: TabId, label: string, iconName: string): string {
    const isActive = this.activeTab === tabId;
    return `
      <button
        type="button"
        role="tab"
        aria-selected="${isActive}"
        data-tab="${tabId}"
        class="flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap transition-colors border-b-2 -mb-px ${
          isActive
            ? 'text-primary border-primary bg-primary/5'
            : 'text-muted-foreground border-transparent hover:text-foreground hover:border-muted-foreground/50'
        }"
      >
        ${renderIcon(iconName as Parameters<typeof renderIcon>[0], { size: 'sm' }).outerHTML}
        <span>${label}</span>
      </button>
    `;
  }

  private renderActiveTabContent(): string {
    switch (this.activeTab) {
      case 'general':
        return this.renderGeneralSettingsTab();
      case 'currencies':
        return this.renderCurrenciesTab();
      case 'executive-approvers':
        return this.renderExecutiveApproversTab();
      case 'quickbooks':
        return this.renderQuickBooksTab();
      case 'audit-log':
        return this.renderAuditLogTab();
      default:
        return '';
    }
  }

  // ============================================================================
  // General Settings Tab
  // ============================================================================

  private renderGeneralSettingsTab(): string {
    if (!this.approvalThresholds) {
      return `<p class="text-muted-foreground">Loading settings...</p>`;
    }

    return `
      <div class="space-y-6">
        <div>
          <h2 class="text-lg font-semibold text-foreground mb-2">Approval Thresholds</h2>
          <p class="text-sm text-muted-foreground mb-4">
            Configure the amount thresholds that determine which approvers are required for expenditure requests.
          </p>
        </div>

        <form id="thresholds-form" class="space-y-4">
          <div class="grid gap-4 md:grid-cols-3">
            <div class="space-y-2">
              <label for="threshold-dept-head" class="block text-sm font-medium text-foreground">
                Department Head Threshold
              </label>
              <div class="relative">
                <span class="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">CHF</span>
                <input
                  type="number"
                  id="threshold-dept-head"
                  name="deptHead"
                  value="${this.approvalThresholds.deptHead}"
                  min="0"
                  step="1"
                  class="w-full pl-12 pr-4 py-2 glass-input rounded-lg text-foreground"
                  required
                />
              </div>
              <p class="text-xs text-muted-foreground">Requests under this amount require only Department Head approval</p>
            </div>

            <div class="space-y-2">
              <label for="threshold-cfo" class="block text-sm font-medium text-foreground">
                CFO Threshold
              </label>
              <div class="relative">
                <span class="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">CHF</span>
                <input
                  type="number"
                  id="threshold-cfo"
                  name="cfo"
                  value="${this.approvalThresholds.cfo}"
                  min="0"
                  step="1"
                  class="w-full pl-12 pr-4 py-2 glass-input rounded-lg text-foreground"
                  required
                />
              </div>
              <p class="text-xs text-muted-foreground">Requests at or above this amount also require CFO approval</p>
            </div>

            <div class="space-y-2">
              <label for="threshold-ceo" class="block text-sm font-medium text-foreground">
                CEO Threshold
              </label>
              <div class="relative">
                <span class="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">CHF</span>
                <input
                  type="number"
                  id="threshold-ceo"
                  name="ceo"
                  value="${this.approvalThresholds.ceo}"
                  min="0"
                  step="1"
                  class="w-full pl-12 pr-4 py-2 glass-input rounded-lg text-foreground"
                  required
                />
              </div>
              <p class="text-xs text-muted-foreground">Requests at or above this amount also require CEO approval</p>
            </div>
          </div>

          <div class="flex justify-end pt-4">
            <button
              type="submit"
              class="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground font-medium rounded-lg hover:bg-primary/90 transition-colors"
            >
              ${renderIcon('save', { size: 'sm' }).outerHTML}
              <span>Save Thresholds</span>
            </button>
          </div>
        </form>
      </div>
    `;
  }

  // ============================================================================
  // Currencies Tab
  // ============================================================================

  private renderCurrenciesTab(): string {
    return `
      <div class="space-y-6">
        <div class="flex items-center justify-between">
          <div>
            <h2 class="text-lg font-semibold text-foreground mb-1">Currency Management</h2>
            <p class="text-sm text-muted-foreground">Add, edit, or remove supported currencies for expenditure requests.</p>
          </div>
          <button
            type="button"
            id="add-currency-btn"
            class="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground font-medium rounded-lg hover:bg-primary/90 transition-colors"
          >
            ${renderIcon('plus', { size: 'sm' }).outerHTML}
            <span>Add Currency</span>
          </button>
        </div>

        <!-- Add Currency Form (Hidden by default) -->
        <div id="add-currency-form-container" class="hidden">
          <form id="add-currency-form" class="glass-card p-4 rounded-lg space-y-4">
            <h3 class="font-medium text-foreground">Add New Currency</h3>
            <div class="grid gap-4 md:grid-cols-4">
              <div class="space-y-1">
                <label for="new-currency-code" class="block text-sm font-medium text-foreground">Code</label>
                <input
                  type="text"
                  id="new-currency-code"
                  name="code"
                  placeholder="USD"
                  maxlength="3"
                  class="w-full px-3 py-2 glass-input rounded-lg text-foreground uppercase"
                  required
                />
              </div>
              <div class="space-y-1">
                <label for="new-currency-name" class="block text-sm font-medium text-foreground">Name</label>
                <input
                  type="text"
                  id="new-currency-name"
                  name="name"
                  placeholder="US Dollar"
                  class="w-full px-3 py-2 glass-input rounded-lg text-foreground"
                  required
                />
              </div>
              <div class="space-y-1">
                <label for="new-currency-symbol" class="block text-sm font-medium text-foreground">Symbol</label>
                <input
                  type="text"
                  id="new-currency-symbol"
                  name="symbol"
                  placeholder="$"
                  maxlength="3"
                  class="w-full px-3 py-2 glass-input rounded-lg text-foreground"
                  required
                />
              </div>
              <div class="flex items-end gap-2">
                <button
                  type="submit"
                  class="flex-1 px-3 py-2 bg-primary text-primary-foreground font-medium rounded-lg hover:bg-primary/90 transition-colors"
                >
                  Add
                </button>
                <button
                  type="button"
                  id="cancel-add-currency-btn"
                  class="px-3 py-2 glass-button rounded-lg text-foreground"
                >
                  Cancel
                </button>
              </div>
            </div>
          </form>
        </div>

        <!-- Currencies Table -->
        <div class="overflow-x-auto">
          <table class="w-full text-sm">
            <thead>
              <tr class="border-b border-border">
                <th class="text-left py-3 px-4 font-medium text-muted-foreground">Code</th>
                <th class="text-left py-3 px-4 font-medium text-muted-foreground">Name</th>
                <th class="text-left py-3 px-4 font-medium text-muted-foreground">Symbol</th>
                <th class="text-center py-3 px-4 font-medium text-muted-foreground">Status</th>
                <th class="text-center py-3 px-4 font-medium text-muted-foreground">Default</th>
                <th class="text-right py-3 px-4 font-medium text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              ${this.currencies.length === 0
                ? `<tr><td colspan="6" class="py-8 text-center text-muted-foreground">No currencies configured</td></tr>`
                : this.currencies.map((c) => this.renderCurrencyRow(c)).join('')}
            </tbody>
          </table>
        </div>
      </div>
    `;
  }

  private renderCurrencyRow(currency: Currency): string {
    return `
      <tr class="border-b border-border hover:bg-muted/20 transition-colors" data-currency-code="${currency.code}">
        <td class="py-3 px-4 font-mono font-medium text-foreground">${this.escapeHtml(currency.code)}</td>
        <td class="py-3 px-4 text-foreground">${this.escapeHtml(currency.name)}</td>
        <td class="py-3 px-4 text-foreground">${this.escapeHtml(currency.symbol)}</td>
        <td class="py-3 px-4 text-center">
          <span class="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
            currency.isActive
              ? 'bg-success/20 text-success'
              : 'bg-muted text-muted-foreground'
          }">
            ${currency.isActive ? 'Active' : 'Inactive'}
          </span>
        </td>
        <td class="py-3 px-4 text-center">
          ${currency.isDefault
            ? `<span class="inline-flex items-center gap-1 text-primary">${renderIcon('check', { size: 'sm' }).outerHTML} Default</span>`
            : `<button type="button" class="set-default-currency-btn text-muted-foreground hover:text-foreground text-xs" data-code="${currency.code}">Set as Default</button>`
          }
        </td>
        <td class="py-3 px-4 text-right">
          <div class="flex items-center justify-end gap-2">
            <button
              type="button"
              class="toggle-currency-btn p-1.5 rounded-lg hover:bg-muted transition-colors"
              data-code="${currency.code}"
              data-active="${currency.isActive}"
              title="${currency.isActive ? 'Deactivate' : 'Activate'}"
            >
              ${currency.isActive
                ? renderIcon('eyeOff', { size: 'sm', className: 'text-muted-foreground' }).outerHTML
                : renderIcon('eye', { size: 'sm', className: 'text-muted-foreground' }).outerHTML
              }
            </button>
            ${!currency.isDefault ? `
              <button
                type="button"
                class="delete-currency-btn p-1.5 rounded-lg hover:bg-destructive/20 transition-colors"
                data-code="${currency.code}"
                title="Delete"
              >
                ${renderIcon('trash', { size: 'sm', className: 'text-destructive' }).outerHTML}
              </button>
            ` : ''}
          </div>
        </td>
      </tr>
    `;
  }

  // ============================================================================
  // Executive Approvers Tab
  // ============================================================================

  private renderExecutiveApproversTab(): string {
    if (!this.executiveApprovers) {
      return `<p class="text-muted-foreground">Loading executive approvers...</p>`;
    }

    return `
      <div class="space-y-6">
        <div>
          <h2 class="text-lg font-semibold text-foreground mb-1">Executive Approvers</h2>
          <p class="text-sm text-muted-foreground">
            Configure which users serve as CFO and CEO for the approval workflow.
          </p>
        </div>

        <div class="grid gap-6 md:grid-cols-2">
          ${this.renderExecutiveApproverCard('CFO', this.executiveApprovers.cfo)}
          ${this.renderExecutiveApproverCard('CEO', this.executiveApprovers.ceo)}
        </div>
      </div>
    `;
  }

  private renderExecutiveApproverCard(role: 'CFO' | 'CEO', approver: ExecutiveApprover | null): string {
    // Add overflow-visible when showing search to prevent dropdown clipping
    const overflowClass = approver ? '' : 'overflow-visible';
    return `
      <div class="glass-card p-4 rounded-lg ${overflowClass}" data-role="${role}">
        <div class="flex items-center justify-between mb-4">
          <h3 class="font-medium text-foreground flex items-center gap-2">
            ${renderIcon('userCheck', { size: 'sm', className: 'text-primary' }).outerHTML}
            ${role}
          </h3>
          ${approver ? `
            <button
              type="button"
              class="remove-approver-btn text-sm text-destructive hover:text-destructive/80 transition-colors"
              data-role="${role}"
            >
              Remove
            </button>
          ` : ''}
        </div>

        ${approver ? `
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
              ${renderIcon('user', { size: 'sm', className: 'text-primary' }).outerHTML}
            </div>
            <div>
              <p class="font-medium text-foreground">${this.escapeHtml(approver.user?.displayName || 'Unknown')}</p>
              <p class="text-sm text-muted-foreground">${this.escapeHtml(approver.user?.email || approver.userId)}</p>
            </div>
          </div>
        ` : `
          <div class="space-y-3">
            <p class="text-sm text-muted-foreground">No ${role} configured. Search for a user to assign.</p>
            <div class="relative">
              <input
                type="text"
                class="user-search-input w-full px-3 py-2 glass-input rounded-lg text-foreground"
                placeholder="Search users by name or email..."
                data-role="${role}"
              />
              <div class="user-search-results absolute z-10 w-full mt-1 glass-card rounded-lg max-h-48 overflow-y-auto hidden" data-role="${role}"></div>
            </div>
          </div>
        `}
      </div>
    `;
  }

  // ============================================================================
  // QuickBooks Tab
  // ============================================================================

  private renderQuickBooksTab(): string {
    if (this.quickBooksLoading) {
      return renderQuickBooksLoading();
    }
    if (this.quickBooksLoadError) {
      return `
        <div class="flex flex-col items-center justify-center py-12 gap-4">
          <div class="w-12 h-12 rounded-full bg-destructive/20 flex items-center justify-center">
            ${renderIcon('alertCircle', { size: 'lg', className: 'text-destructive' }).outerHTML}
          </div>
          <p class="text-destructive text-sm">${this.escapeHtml(this.quickBooksLoadError)}</p>
          <button type="button" id="qb-retry-btn" class="inline-flex items-center gap-2 px-4 py-2 glass-button rounded-lg text-foreground">
            ${renderIcon('refresh', { size: 'sm' }).outerHTML}
            <span>Retry</span>
          </button>
        </div>
      `;
    }
    return renderQuickBooksConnectionStatus(this.quickBooksStatus);
  }

  // ============================================================================
  // Audit Log Tab
  // ============================================================================

  private renderAuditLogTab(): string {
    return `
      <div class="space-y-6">
        <div class="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h2 class="text-lg font-semibold text-foreground mb-1">Audit Log</h2>
            <p class="text-sm text-muted-foreground">View system activity and track changes made by users.</p>
          </div>
          <button
            type="button"
            id="load-audit-log-btn"
            class="inline-flex items-center gap-2 px-4 py-2 glass-button rounded-lg text-foreground"
          >
            ${renderIcon('refresh', { size: 'sm' }).outerHTML}
            <span>Load / Refresh</span>
          </button>
        </div>

        <!-- Search and Filters -->
        <div class="space-y-4">
          <!-- Search by Actor or Request ID -->
          <div class="flex flex-wrap gap-4">
            <div class="flex-1 min-w-64">
              <label for="audit-search" class="block text-sm font-medium text-muted-foreground mb-1">Search by Actor ID or Request ID</label>
              <div class="relative">
                <input
                  type="text"
                  id="audit-search"
                  placeholder="Enter user ID or request ID..."
                  value="${this.escapeHtml(this.auditLogSearchQuery)}"
                  class="w-full pl-10 pr-4 py-2 glass-input rounded-lg text-foreground font-mono text-sm"
                />
                <div class="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                  ${renderIcon('search', { size: 'sm' }).outerHTML}
                </div>
              </div>
              <p class="text-xs text-muted-foreground mt-1">Search will match actor user ID or request ID</p>
            </div>
          </div>

          <!-- Filters Row -->
          <div class="flex flex-wrap gap-4">
            <div class="flex-1 min-w-48">
              <label for="audit-action-filter" class="block text-sm font-medium text-muted-foreground mb-1">Action Type</label>
              <select id="audit-action-filter" class="w-full px-3 py-2 glass-input rounded-lg text-foreground">
                <option value=""${!this.auditLogFilter.action ? ' selected' : ''}>All Actions</option>
                <option value="REQUEST_CREATED"${this.auditLogFilter.action === 'REQUEST_CREATED' ? ' selected' : ''}>Request Created</option>
                <option value="REQUEST_UPDATED"${this.auditLogFilter.action === 'REQUEST_UPDATED' ? ' selected' : ''}>Request Updated</option>
                <option value="REQUEST_SUBMITTED"${this.auditLogFilter.action === 'REQUEST_SUBMITTED' ? ' selected' : ''}>Request Submitted</option>
                <option value="REQUEST_CANCELLED"${this.auditLogFilter.action === 'REQUEST_CANCELLED' ? ' selected' : ''}>Request Cancelled</option>
                <option value="APPROVAL_GRANTED"${this.auditLogFilter.action === 'APPROVAL_GRANTED' ? ' selected' : ''}>Approval Granted</option>
                <option value="APPROVAL_REJECTED"${this.auditLogFilter.action === 'APPROVAL_REJECTED' ? ' selected' : ''}>Approval Rejected</option>
                <option value="APPROVAL_DELEGATED"${this.auditLogFilter.action === 'APPROVAL_DELEGATED' ? ' selected' : ''}>Approval Delegated</option>
                <option value="APPROVAL_REASSIGNED"${this.auditLogFilter.action === 'APPROVAL_REASSIGNED' ? ' selected' : ''}>Approval Reassigned</option>
                <option value="COMMENT_ADDED"${this.auditLogFilter.action === 'COMMENT_ADDED' ? ' selected' : ''}>Comment Added</option>
                <option value="ATTACHMENT_ADDED"${this.auditLogFilter.action === 'ATTACHMENT_ADDED' ? ' selected' : ''}>Attachment Added</option>
                <option value="ATTACHMENT_REMOVED"${this.auditLogFilter.action === 'ATTACHMENT_REMOVED' ? ' selected' : ''}>Attachment Removed</option>
                <option value="DELEGATION_CREATED"${this.auditLogFilter.action === 'DELEGATION_CREATED' ? ' selected' : ''}>Delegation Created</option>
                <option value="DELEGATION_REVOKED"${this.auditLogFilter.action === 'DELEGATION_REVOKED' ? ' selected' : ''}>Delegation Revoked</option>
                <option value="EXECUTIVE_APPROVER_ADDED"${this.auditLogFilter.action === 'EXECUTIVE_APPROVER_ADDED' ? ' selected' : ''}>Executive Approver Added</option>
                <option value="EXECUTIVE_APPROVER_REMOVED"${this.auditLogFilter.action === 'EXECUTIVE_APPROVER_REMOVED' ? ' selected' : ''}>Executive Approver Removed</option>
                <option value="SYSTEM_SETTING_CHANGED"${this.auditLogFilter.action === 'SYSTEM_SETTING_CHANGED' ? ' selected' : ''}>System Setting Changed</option>
              </select>
            </div>
            <div class="w-40">
              <label for="audit-from-date" class="block text-sm font-medium text-muted-foreground mb-1">From Date</label>
              <input
                type="date"
                id="audit-from-date"
                value="${this.auditLogFilter.fromDate ? this.auditLogFilter.fromDate.split('T')[0] : ''}"
                class="w-full px-3 py-2 glass-input rounded-lg text-foreground"
              />
            </div>
            <div class="w-40">
              <label for="audit-to-date" class="block text-sm font-medium text-muted-foreground mb-1">To Date</label>
              <input
                type="date"
                id="audit-to-date"
                value="${this.auditLogFilter.toDate ? this.auditLogFilter.toDate.split('T')[0] : ''}"
                class="w-full px-3 py-2 glass-input rounded-lg text-foreground"
              />
            </div>
            <div class="flex items-end">
              <button
                type="button"
                id="clear-audit-filters-btn"
                class="px-3 py-2 text-sm glass-button rounded-lg text-muted-foreground hover:text-foreground transition-colors"
                title="Clear all filters"
              >
                ${renderIcon('x', { size: 'sm' }).outerHTML}
                <span class="ml-1">Clear</span>
              </button>
            </div>
          </div>
        </div>

        <!-- Audit Log Table -->
        <div id="audit-log-container">
          ${this.auditLogEntries.length === 0 && this.auditLogPagination.total === 0
            ? `<div class="text-center py-12 text-muted-foreground">
                <p>Click "Load / Refresh" to view audit log entries.</p>
              </div>`
            : this.renderAuditLogTable()
          }
        </div>
      </div>
    `;
  }

  private renderAuditLogTable(): string {
    if (this.auditLogEntries.length === 0) {
      return `<div class="text-center py-12 text-muted-foreground">No audit log entries found.</div>`;
    }

    return `
      <div class="overflow-x-auto">
        <table class="w-full text-sm">
          <thead>
            <tr class="border-b border-border">
              <th class="w-8 py-3 px-2"></th>
              <th class="text-left py-3 px-4 font-medium text-muted-foreground">Timestamp</th>
              <th class="text-left py-3 px-4 font-medium text-muted-foreground">Actor</th>
              <th class="text-left py-3 px-4 font-medium text-muted-foreground">Action</th>
              <th class="text-left py-3 px-4 font-medium text-muted-foreground">Request ID</th>
              <th class="text-left py-3 px-4 font-medium text-muted-foreground">Details</th>
            </tr>
          </thead>
          <tbody>
            ${this.auditLogEntries.map((entry) => this.renderAuditLogRow(entry)).join('')}
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div class="flex items-center justify-between pt-4 border-t border-border">
        <p class="text-sm text-muted-foreground">
          Showing ${(this.auditLogPagination.page - 1) * this.auditLogPagination.limit + 1} -
          ${Math.min(this.auditLogPagination.page * this.auditLogPagination.limit, this.auditLogPagination.total)}
          of ${this.auditLogPagination.total} entries
        </p>
        <div class="flex items-center gap-2">
          <button
            type="button"
            id="audit-prev-page"
            class="px-3 py-1.5 glass-button rounded-lg text-sm disabled:opacity-50"
            ${this.auditLogPagination.page <= 1 ? 'disabled' : ''}
          >
            Previous
          </button>
          <span class="text-sm text-muted-foreground">
            Page ${this.auditLogPagination.page} of ${this.auditLogPagination.totalPages}
          </span>
          <button
            type="button"
            id="audit-next-page"
            class="px-3 py-1.5 glass-button rounded-lg text-sm disabled:opacity-50"
            ${!this.auditLogPagination.hasMore ? 'disabled' : ''}
          >
            Next
          </button>
        </div>
      </div>
    `;
  }

  private renderAuditLogRow(entry: AuditLogEntry): string {
    const isExpanded = this.auditLogExpandedRows.has(entry.id);
    const date = new Date(entry.created_at);
    const formattedDate = date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
    const formattedTime = date.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });

    let parsedDetails: Record<string, unknown> = {};
    let detailsSummary = '';
    try {
      parsedDetails = JSON.parse(entry.details);
      // Create a summary of key details
      const summaryParts: string[] = [];
      if (parsedDetails.requestTitle) summaryParts.push(`Title: ${parsedDetails.requestTitle}`);
      if (parsedDetails.amount) summaryParts.push(`Amount: ${parsedDetails.amount}`);
      if (parsedDetails.status) summaryParts.push(`Status: ${parsedDetails.status}`);
      if (parsedDetails.role) summaryParts.push(`Role: ${parsedDetails.role}`);
      if (parsedDetails.settingKey) summaryParts.push(`Setting: ${parsedDetails.settingKey}`);
      detailsSummary = summaryParts.length > 0 ? summaryParts.join(' | ') : 'Click to view details';
    } catch {
      detailsSummary = entry.details;
    }

    const actionLabel = entry.action.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase());
    const actorDisplayName = this.userNameCache.get(entry.user_id) || null;
    const actorDisplay = actorDisplayName
      ? `<span class="text-foreground">${this.escapeHtml(actorDisplayName)}</span><br/><span class="font-mono text-muted-foreground text-xs">${this.escapeHtml(entry.user_id.substring(0, 8))}...</span>`
      : `<span class="font-mono text-foreground">${this.escapeHtml(entry.user_id.substring(0, 8))}...</span>`;

    const requestIdDisplay = entry.request_id
      ? `<span class="font-mono text-xs">${this.escapeHtml(entry.request_id.substring(0, 8))}...</span>`
      : `<span class="text-muted-foreground">-</span>`;

    // Main row
    let html = `
      <tr class="border-b border-border hover:bg-muted/20 transition-colors cursor-pointer audit-row" data-entry-id="${entry.id}">
        <td class="py-3 px-2 text-center">
          <button type="button" class="expand-audit-row-btn p-1 rounded hover:bg-muted/50 transition-colors" data-entry-id="${entry.id}" title="${isExpanded ? 'Collapse' : 'Expand'}">
            ${renderIcon(isExpanded ? 'chevronDown' : 'chevronRight', { size: 'sm', className: 'text-muted-foreground' }).outerHTML}
          </button>
        </td>
        <td class="py-3 px-4 whitespace-nowrap">
          <div class="text-foreground">${formattedDate}</div>
          <div class="text-xs text-muted-foreground font-mono">${formattedTime}</div>
        </td>
        <td class="py-3 px-4">${actorDisplay}</td>
        <td class="py-3 px-4">
          <span class="inline-flex items-center px-2 py-1 rounded text-xs font-medium ${this.getActionColorClass(entry.action)}">
            ${actionLabel}
          </span>
        </td>
        <td class="py-3 px-4">${requestIdDisplay}</td>
        <td class="py-3 px-4 text-muted-foreground text-xs max-w-xs truncate">
          ${this.escapeHtml(detailsSummary.substring(0, 60))}${detailsSummary.length > 60 ? '...' : ''}
        </td>
      </tr>
    `;

    // Expanded details row
    if (isExpanded) {
      const formattedJson = JSON.stringify(parsedDetails, null, 2);
      html += `
        <tr class="bg-muted/10 border-b border-border">
          <td colspan="6" class="p-4">
            <div class="space-y-4">
              <!-- Full Details Header -->
              <div class="flex items-center justify-between">
                <h4 class="text-sm font-medium text-foreground flex items-center gap-2">
                  ${renderIcon('fileText', { size: 'sm', className: 'text-primary' }).outerHTML}
                  Full Audit Entry Details
                </h4>
                <button
                  type="button"
                  class="copy-audit-json-btn px-2 py-1 text-xs glass-button rounded text-muted-foreground hover:text-foreground"
                  data-json="${this.escapeHtml(JSON.stringify(parsedDetails))}"
                  title="Copy JSON to clipboard"
                >
                  ${renderIcon('copy', { size: 'xs' }).outerHTML}
                  <span class="ml-1">Copy</span>
                </button>
              </div>

              <!-- Entry Metadata -->
              <div class="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <span class="text-muted-foreground">Entry ID:</span>
                  <span class="ml-1 font-mono text-xs text-foreground">${this.escapeHtml(entry.id)}</span>
                </div>
                <div>
                  <span class="text-muted-foreground">Actor ID:</span>
                  <span class="ml-1 font-mono text-xs text-foreground">${this.escapeHtml(entry.user_id)}</span>
                </div>
                ${entry.request_id ? `
                  <div>
                    <span class="text-muted-foreground">Request ID:</span>
                    <span class="ml-1 font-mono text-xs text-foreground">${this.escapeHtml(entry.request_id)}</span>
                  </div>
                ` : ''}
                <div>
                  <span class="text-muted-foreground">Timestamp:</span>
                  <span class="ml-1 text-foreground">${entry.created_at}</span>
                </div>
              </div>

              ${entry.ip_address || entry.user_agent ? `
                <!-- Client Info -->
                <div class="space-y-1 text-sm">
                  ${entry.ip_address ? `
                    <div>
                      <span class="text-muted-foreground">IP Address:</span>
                      <span class="ml-1 font-mono text-xs text-foreground">${this.escapeHtml(entry.ip_address)}</span>
                    </div>
                  ` : ''}
                  ${entry.user_agent ? `
                    <div>
                      <span class="text-muted-foreground">User Agent:</span>
                      <span class="ml-1 text-xs text-foreground truncate block">${this.escapeHtml(entry.user_agent)}</span>
                    </div>
                  ` : ''}
                </div>
              ` : ''}

              <!-- JSON Details -->
              <div class="rounded-lg bg-background/50 border border-border overflow-hidden">
                <div class="px-3 py-2 bg-muted/30 border-b border-border">
                  <span class="text-xs font-medium text-muted-foreground">Details (JSON)</span>
                </div>
                <pre class="p-4 text-xs font-mono text-foreground overflow-x-auto whitespace-pre-wrap break-words">${this.escapeHtml(formattedJson)}</pre>
              </div>
            </div>
          </td>
        </tr>
      `;
    }

    return html;
  }

  private getActionColorClass(action: string): string {
    if (action.includes('CREATED') || action.includes('ADDED') || action.includes('GRANTED')) {
      return 'bg-success/20 text-success';
    }
    if (action.includes('REMOVED') || action.includes('REJECTED') || action.includes('CANCELLED') || action.includes('REVOKED')) {
      return 'bg-destructive/20 text-destructive';
    }
    if (action.includes('UPDATED') || action.includes('CHANGED') || action.includes('DELEGATED')) {
      return 'bg-warning/20 text-warning';
    }
    return 'bg-muted text-muted-foreground';
  }

  // ============================================================================
  // Alert Messages
  // ============================================================================

  private renderAlerts(): void {
    const alertContainer = this.container.querySelector('#alert-container');
    if (!alertContainer) return;

    let alertHtml = '';

    if (this.errorMessage) {
      alertHtml += `
        <div class="flex items-center gap-3 p-4 rounded-lg bg-destructive/20 border border-destructive/30 text-destructive" role="alert">
          ${renderIcon('alertCircle', { size: 'sm' }).outerHTML}
          <span class="flex-1">${this.escapeHtml(this.errorMessage)}</span>
          <button type="button" class="dismiss-alert text-destructive/70 hover:text-destructive">&times;</button>
        </div>
      `;
    }

    if (this.successMessage) {
      alertHtml += `
        <div class="flex items-center gap-3 p-4 rounded-lg bg-success/20 border border-success/30 text-success" role="alert">
          ${renderIcon('checkCircle', { size: 'sm' }).outerHTML}
          <span class="flex-1">${this.escapeHtml(this.successMessage)}</span>
          <button type="button" class="dismiss-alert text-success/70 hover:text-success">&times;</button>
        </div>
      `;
    }

    alertContainer.innerHTML = alertHtml;

    // Attach dismiss listeners
    alertContainer.querySelectorAll('.dismiss-alert').forEach((btn) => {
      btn.addEventListener('click', () => {
        this.errorMessage = null;
        this.successMessage = null;
        this.renderAlerts();
      });
    });
  }

  private renderError(): void {
    this.container.innerHTML = `
      <div class="min-h-screen flex items-center justify-center p-4">
        <div class="glass-card w-full max-w-md p-8 rounded-2xl text-center">
          <div class="flex justify-center mb-4">
            <div class="w-16 h-16 rounded-full bg-destructive/20 flex items-center justify-center">
              ${renderIcon('alertCircle', { size: 'xl', className: 'text-destructive' }).outerHTML}
            </div>
          </div>
          <h1 class="text-xl font-bold text-foreground mb-2">Error Loading Settings</h1>
          <p class="text-muted-foreground mb-6">${this.escapeHtml(this.errorMessage || 'An unexpected error occurred')}</p>
          <button type="button" id="retry-btn" class="inline-flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground font-medium rounded-lg hover:bg-primary/90 transition-colors">
            ${renderIcon('refresh', { size: 'sm' }).outerHTML}
            <span>Try Again</span>
          </button>
        </div>
      </div>
    `;

    this.container.querySelector('#retry-btn')?.addEventListener('click', () => {
      this.init();
    });
  }

  // ============================================================================
  // Event Listeners
  // ============================================================================

  private attachEventListeners(): void {
    // Tab navigation
    this.container.querySelectorAll('[data-tab]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const tabId = btn.getAttribute('data-tab') as TabId;
        this.switchTab(tabId);
      });
    });

    // Tab-specific listeners
    this.attachGeneralSettingsListeners();
    this.attachCurrenciesListeners();
    this.attachExecutiveApproversListeners();
    this.attachQuickBooksListeners();
    this.attachAuditLogListeners();
  }

  private switchTab(tabId: TabId): void {
    this.activeTab = tabId;
    this.render();

    // Auto-load audit log data when switching to that tab
    if (tabId === 'audit-log' && this.auditLogEntries.length === 0) {
      this.loadAuditLog();
    }

    // Auto-load QuickBooks status when switching to that tab
    if (tabId === 'quickbooks' && this.quickBooksStatus === null) {
      this.loadQuickBooksStatus();
    }
  }

  private attachGeneralSettingsListeners(): void {
    const form = this.container.querySelector('#thresholds-form') as HTMLFormElement;
    if (form) {
      form.addEventListener('submit', async (e) => {
        e.preventDefault();
        await this.handleSaveThresholds(new FormData(form));
      });
    }
  }

  private async handleSaveThresholds(formData: FormData): Promise<void> {
    const deptHead = parseInt(formData.get('deptHead') as string, 10);
    const cfo = parseInt(formData.get('cfo') as string, 10);
    const ceo = parseInt(formData.get('ceo') as string, 10);

    // Validate thresholds order
    if (!(deptHead <= cfo && cfo <= ceo)) {
      this.errorMessage = 'Thresholds must be in ascending order: Department Head <= CFO <= CEO';
      this.successMessage = null;
      this.renderAlerts();
      return;
    }

    try {
      await apiClient.put('/admin/settings', {
        approvalThresholds: { deptHead, cfo, ceo },
      });

      this.approvalThresholds = { deptHead, cfo, ceo };
      this.successMessage = 'Approval thresholds updated successfully';
      this.errorMessage = null;
      this.renderAlerts();
    } catch (error) {
      this.errorMessage = (error as { message?: string }).message || 'Failed to update thresholds';
      this.successMessage = null;
      this.renderAlerts();
    }
  }

  private attachCurrenciesListeners(): void {
    // Show add currency form
    const addBtn = this.container.querySelector('#add-currency-btn');
    const formContainer = this.container.querySelector('#add-currency-form-container');
    const cancelBtn = this.container.querySelector('#cancel-add-currency-btn');
    const addForm = this.container.querySelector('#add-currency-form') as HTMLFormElement;

    addBtn?.addEventListener('click', () => {
      formContainer?.classList.remove('hidden');
    });

    cancelBtn?.addEventListener('click', () => {
      formContainer?.classList.add('hidden');
      addForm?.reset();
    });

    // Add currency form submit
    addForm?.addEventListener('submit', async (e) => {
      e.preventDefault();
      await this.handleAddCurrency(new FormData(addForm));
    });

    // Toggle currency active state
    this.container.querySelectorAll('.toggle-currency-btn').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const code = btn.getAttribute('data-code');
        const isActive = btn.getAttribute('data-active') === 'true';
        if (code) {
          await this.handleToggleCurrency(code, !isActive);
        }
      });
    });

    // Set default currency
    this.container.querySelectorAll('.set-default-currency-btn').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const code = btn.getAttribute('data-code');
        if (code) {
          await this.handleSetDefaultCurrency(code);
        }
      });
    });

    // Delete currency
    this.container.querySelectorAll('.delete-currency-btn').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const code = btn.getAttribute('data-code');
        if (code) {
          const confirmed = await confirmModal({
            title: 'Delete Currency',
            message: `Are you sure you want to delete currency ${code}?`,
            confirmText: 'Delete',
            cancelText: 'Cancel',
            destructive: true,
          });
          if (confirmed) {
            await this.handleDeleteCurrency(code);
          }
        }
      });
    });
  }

  private async handleAddCurrency(formData: FormData): Promise<void> {
    const code = (formData.get('code') as string).toUpperCase();
    const name = formData.get('name') as string;
    const symbol = formData.get('symbol') as string;

    try {
      await apiClient.post('/admin/currencies', { code, name, symbol });
      this.successMessage = `Currency ${code} added successfully`;
      this.errorMessage = null;
      // Reload currencies
      const response = await apiClient.get<{ data: Currency[] }>('/admin/currencies');
      this.currencies = response.data.data;
      this.render();
    } catch (error) {
      this.errorMessage = (error as { message?: string }).message || 'Failed to add currency';
      this.successMessage = null;
      this.renderAlerts();
    }
  }

  private async handleToggleCurrency(code: string, isActive: boolean): Promise<void> {
    try {
      await apiClient.put(`/admin/currencies/${code}`, { isActive });
      this.successMessage = `Currency ${code} ${isActive ? 'activated' : 'deactivated'}`;
      this.errorMessage = null;
      // Update local state
      const currency = this.currencies.find((c) => c.code === code);
      if (currency) {
        currency.isActive = isActive;
      }
      this.render();
    } catch (error) {
      this.errorMessage = (error as { message?: string }).message || 'Failed to update currency';
      this.successMessage = null;
      this.renderAlerts();
    }
  }

  private async handleSetDefaultCurrency(code: string): Promise<void> {
    try {
      await apiClient.put(`/admin/currencies/${code}`, { isDefault: true });
      this.successMessage = `${code} is now the default currency`;
      this.errorMessage = null;
      // Reload currencies
      const response = await apiClient.get<{ data: Currency[] }>('/admin/currencies');
      this.currencies = response.data.data;
      this.render();
    } catch (error) {
      this.errorMessage = (error as { message?: string }).message || 'Failed to set default currency';
      this.successMessage = null;
      this.renderAlerts();
    }
  }

  private async handleDeleteCurrency(code: string): Promise<void> {
    try {
      await apiClient.delete(`/admin/currencies/${code}`);
      this.successMessage = `Currency ${code} deleted`;
      this.errorMessage = null;
      this.currencies = this.currencies.filter((c) => c.code !== code);
      this.render();
    } catch (error) {
      this.errorMessage = (error as { message?: string }).message || 'Failed to delete currency';
      this.successMessage = null;
      this.renderAlerts();
    }
  }

  private attachExecutiveApproversListeners(): void {
    // User search inputs
    this.container.querySelectorAll('.user-search-input').forEach((input) => {
      let debounceTimer: ReturnType<typeof setTimeout>;

      input.addEventListener('input', (e) => {
        const target = e.target as HTMLInputElement;
        const role = target.getAttribute('data-role') as 'CFO' | 'CEO';
        const query = target.value.trim();

        clearTimeout(debounceTimer);
        if (query.length >= 2) {
          debounceTimer = setTimeout(() => this.searchUsers(query, role), 300);
        } else {
          this.hideUserSearchResults(role);
        }
      });

      input.addEventListener('blur', () => {
        // Delay hiding to allow click on results
        setTimeout(() => {
          const role = input.getAttribute('data-role') as 'CFO' | 'CEO';
          this.hideUserSearchResults(role);
        }, 200);
      });
    });

    // Remove approver buttons
    this.container.querySelectorAll('.remove-approver-btn').forEach((btn) => {
      btn.addEventListener('click', async () => {
        const role = btn.getAttribute('data-role') as 'CFO' | 'CEO';
        const confirmed = await confirmModal({
          title: `Remove ${role}`,
          message: `Are you sure you want to remove the ${role}?`,
          confirmText: 'Remove',
          cancelText: 'Cancel',
          destructive: true,
        });
        if (confirmed) {
          await this.handleRemoveApprover(role);
        }
      });
    });
  }

  private async searchUsers(query: string, role: 'CFO' | 'CEO'): Promise<void> {
    try {
      const response = await apiClient.get<{ data: User[] }>('/users/search', {
        params: { q: query, limit: 5 },
      });

      this.userSearchResults = response.data.data;
      this.showUserSearchResults(role);
    } catch (error) {
      console.error('User search failed:', error);
      this.userSearchResults = [];
    }
  }

  private showUserSearchResults(role: 'CFO' | 'CEO'): void {
    const resultsContainer = this.container.querySelector(`.user-search-results[data-role="${role}"]`);
    if (!resultsContainer) return;

    if (this.userSearchResults.length === 0) {
      resultsContainer.innerHTML = `<div class="p-3 text-sm text-muted-foreground">No users found</div>`;
    } else {
      resultsContainer.innerHTML = this.userSearchResults
        .map(
          (user) => `
          <button
            type="button"
            class="user-search-result w-full text-left px-3 py-2 hover:bg-muted/50 transition-colors flex items-center gap-2"
            data-user-id="${user.id}"
            data-role="${role}"
          >
            <div class="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
              ${renderIcon('user', { size: 'xs', className: 'text-primary' }).outerHTML}
            </div>
            <div class="min-w-0">
              <p class="font-medium text-foreground truncate">${this.escapeHtml(user.displayName)}</p>
              <p class="text-xs text-muted-foreground truncate">${this.escapeHtml(user.email)}</p>
            </div>
          </button>
        `
        )
        .join('');

      // Attach mousedown handlers (mousedown fires before blur, preventing race condition)
      resultsContainer.querySelectorAll('.user-search-result').forEach((btn) => {
        btn.addEventListener('mousedown', async (e) => {
          e.preventDefault(); // Prevent blur from firing
          const userId = btn.getAttribute('data-user-id');
          const btnRole = btn.getAttribute('data-role') as 'CFO' | 'CEO';
          if (userId) {
            await this.handleSetApprover(btnRole, userId);
          }
        });
      });
    }

    resultsContainer.classList.remove('hidden');
  }

  private hideUserSearchResults(role: 'CFO' | 'CEO'): void {
    const resultsContainer = this.container.querySelector(`.user-search-results[data-role="${role}"]`);
    resultsContainer?.classList.add('hidden');
  }

  private async handleSetApprover(role: 'CFO' | 'CEO', userId: string): Promise<void> {
    try {
      const body = role === 'CFO' ? { cfo: { userId } } : { ceo: { userId } };
      await apiClient.put('/admin/executive-approvers', body);

      this.successMessage = `${role} set successfully`;
      this.errorMessage = null;

      // Reload executive approvers
      const response = await apiClient.get<{ data: ExecutiveApproversConfig }>('/admin/executive-approvers');
      this.executiveApprovers = response.data.data;
      this.render();
    } catch (error) {
      this.errorMessage = (error as { message?: string }).message || `Failed to set ${role}`;
      this.successMessage = null;
      this.renderAlerts();
    }
  }

  private async handleRemoveApprover(role: 'CFO' | 'CEO'): Promise<void> {
    try {
      const body = role === 'CFO' ? { cfo: null } : { ceo: null };
      await apiClient.put('/admin/executive-approvers', body);

      this.successMessage = `${role} removed successfully`;
      this.errorMessage = null;

      // Reload executive approvers
      const response = await apiClient.get<{ data: ExecutiveApproversConfig }>('/admin/executive-approvers');
      this.executiveApprovers = response.data.data;
      this.render();
    } catch (error) {
      this.errorMessage = (error as { message?: string }).message || `Failed to remove ${role}`;
      this.successMessage = null;
      this.renderAlerts();
    }
  }

  // ============================================================================
  // QuickBooks Integration Methods
  // ============================================================================

  private attachQuickBooksListeners(): void {
    attachQuickBooksConnectionListeners(this.container, {
      onConnect: () => this.handleQuickBooksConnect(),
      onDisconnect: () => this.handleQuickBooksDisconnect(),
      onReconnect: () => this.handleQuickBooksConnect(), // Reconnect triggers same OAuth flow
    });

    // Retry button for error state
    const retryBtn = this.container.querySelector('#qb-retry-btn');
    retryBtn?.addEventListener('click', () => this.loadQuickBooksStatus());
  }

  private async loadQuickBooksStatus(): Promise<void> {
    this.quickBooksLoading = true;
    this.quickBooksLoadError = null;
    this.render();

    try {
      const response = await apiClient.get<QuickBooksConnectionStatus>('/quickbooks/status');
      this.quickBooksStatus = response.data;
    } catch (error) {
      console.error('Failed to load QuickBooks status:', error);
      this.quickBooksLoadError = (error as { message?: string }).message || 'Failed to load QuickBooks status';
      this.quickBooksStatus = null;
    } finally {
      this.quickBooksLoading = false;
      this.render();
    }
  }

  private async handleQuickBooksConnect(): Promise<void> {
    try {
      const response = await apiClient.get<{ authUrl: string }>('/quickbooks/connect');
      const authUrl = response.data.authUrl;

      if (authUrl) {
        // Redirect to QuickBooks authorization page
        window.location.href = authUrl;
      } else {
        throw new Error('No authorization URL received');
      }
    } catch (error) {
      this.errorMessage = (error as { message?: string }).message || 'Failed to initiate QuickBooks connection';
      this.successMessage = null;
      this.renderAlerts();
    }
  }

  private async handleQuickBooksDisconnect(): Promise<void> {
    const confirmed = await confirmModal({
      title: 'Disconnect QuickBooks',
      message: 'Are you sure you want to disconnect from QuickBooks? Team Assistants will no longer be able to sync expense reports until reconnected.',
      confirmText: 'Disconnect',
      cancelText: 'Cancel',
      destructive: true,
    });

    if (!confirmed) {
      return;
    }

    try {
      await apiClient.post('/quickbooks/disconnect');
      toastSuccess('QuickBooks disconnected successfully');
      this.quickBooksStatus = { connected: false, status: 'not_connected' };
      this.render();
    } catch (error) {
      toastError((error as { message?: string }).message || 'Failed to disconnect from QuickBooks');
    }
  }

  private attachAuditLogListeners(): void {
    // Load/Refresh button
    const loadBtn = this.container.querySelector('#load-audit-log-btn');
    loadBtn?.addEventListener('click', () => this.loadAuditLog());

    // Search input with debounce
    const searchInput = this.container.querySelector('#audit-search') as HTMLInputElement;
    let searchDebounceTimer: ReturnType<typeof setTimeout>;
    searchInput?.addEventListener('input', () => {
      clearTimeout(searchDebounceTimer);
      searchDebounceTimer = setTimeout(() => {
        this.auditLogSearchQuery = searchInput.value.trim();
        this.applySearchFilter();
        this.auditLogPagination.page = 1;
        this.loadAuditLog();
      }, 300);
    });

    // Clear filters button
    const clearBtn = this.container.querySelector('#clear-audit-filters-btn');
    clearBtn?.addEventListener('click', () => {
      this.clearAuditLogFilters();
    });

    // Filter changes
    const actionFilter = this.container.querySelector('#audit-action-filter') as HTMLSelectElement;
    const fromDateFilter = this.container.querySelector('#audit-from-date') as HTMLInputElement;
    const toDateFilter = this.container.querySelector('#audit-to-date') as HTMLInputElement;

    actionFilter?.addEventListener('change', () => {
      if (actionFilter.value) {
        this.auditLogFilter.action = actionFilter.value;
      } else {
        delete this.auditLogFilter.action;
      }
      this.auditLogPagination.page = 1;
      this.loadAuditLog();
    });

    fromDateFilter?.addEventListener('change', () => {
      if (fromDateFilter.value) {
        this.auditLogFilter.fromDate = `${fromDateFilter.value}T00:00:00`;
      } else {
        delete this.auditLogFilter.fromDate;
      }
      this.auditLogPagination.page = 1;
      this.loadAuditLog();
    });

    toDateFilter?.addEventListener('change', () => {
      if (toDateFilter.value) {
        this.auditLogFilter.toDate = `${toDateFilter.value}T23:59:59`;
      } else {
        delete this.auditLogFilter.toDate;
      }
      this.auditLogPagination.page = 1;
      this.loadAuditLog();
    });

    // Pagination
    const prevBtn = this.container.querySelector('#audit-prev-page');
    const nextBtn = this.container.querySelector('#audit-next-page');

    prevBtn?.addEventListener('click', () => {
      if (this.auditLogPagination.page > 1) {
        this.auditLogPagination.page--;
        this.loadAuditLog();
      }
    });

    nextBtn?.addEventListener('click', () => {
      if (this.auditLogPagination.hasMore) {
        this.auditLogPagination.page++;
        this.loadAuditLog();
      }
    });
  }

  private applySearchFilter(): void {
    // The search query can match either userId or requestId
    // We'll use userId filter primarily, but if it looks like a request ID format, we'll use requestId
    const query = this.auditLogSearchQuery;
    if (!query) {
      delete this.auditLogFilter.userId;
      delete this.auditLogFilter.requestId;
      return;
    }

    // If query looks like it could be a UUID or partial UUID, try both filters
    // We'll primarily search by userId, and the API will handle it
    this.auditLogFilter.userId = query;
    // If user wants to search by request ID specifically, they can use the full ID
    // or we could add a separate field, but for simplicity we'll search userId first
  }

  private clearAuditLogFilters(): void {
    this.auditLogSearchQuery = '';
    this.auditLogFilter = {};
    this.auditLogPagination.page = 1;
    this.auditLogExpandedRows.clear();

    // Update UI
    const searchInput = this.container.querySelector('#audit-search') as HTMLInputElement;
    const actionFilter = this.container.querySelector('#audit-action-filter') as HTMLSelectElement;
    const fromDateFilter = this.container.querySelector('#audit-from-date') as HTMLInputElement;
    const toDateFilter = this.container.querySelector('#audit-to-date') as HTMLInputElement;

    if (searchInput) searchInput.value = '';
    if (actionFilter) actionFilter.value = '';
    if (fromDateFilter) fromDateFilter.value = '';
    if (toDateFilter) toDateFilter.value = '';

    this.loadAuditLog();
  }

  private async loadAuditLog(): Promise<void> {
    const container = this.container.querySelector('#audit-log-container');
    if (!container) return;

    container.innerHTML = `
      <div class="flex items-center justify-center py-12">
        ${renderIcon('loader', { size: 'lg', className: 'animate-spin text-primary' }).outerHTML}
      </div>
    `;

    try {
      const params: Record<string, string | number> = {
        page: this.auditLogPagination.page,
        limit: this.auditLogPagination.limit,
      };

      if (this.auditLogFilter.action) {
        params.action = this.auditLogFilter.action;
      }
      if (this.auditLogFilter.userId) {
        params.userId = this.auditLogFilter.userId;
      }
      if (this.auditLogFilter.requestId) {
        params.requestId = this.auditLogFilter.requestId;
      }
      if (this.auditLogFilter.fromDate) {
        params.fromDate = this.auditLogFilter.fromDate;
      }
      if (this.auditLogFilter.toDate) {
        params.toDate = this.auditLogFilter.toDate;
      }

      const response = await apiClient.get<{
        data: {
          entries: AuditLogEntry[];
          pagination: AuditLogPagination;
        };
      }>('/admin/audit-log', { params });

      this.auditLogEntries = response.data.data.entries;
      this.auditLogPagination = response.data.data.pagination;

      // Try to load user names for display
      await this.loadUserNamesForAuditLog();

      container.innerHTML = this.renderAuditLogTable();
      this.attachAuditLogTableListeners();
    } catch (error) {
      container.innerHTML = `
        <div class="text-center py-12">
          <p class="text-destructive">${this.escapeHtml((error as { message?: string }).message || 'Failed to load audit log')}</p>
        </div>
      `;
    }
  }

  private async loadUserNamesForAuditLog(): Promise<void> {
    // Collect unique user IDs that we don't have cached
    const userIds = new Set<string>();
    for (const entry of this.auditLogEntries) {
      if (!this.userNameCache.has(entry.user_id)) {
        userIds.add(entry.user_id);
      }
    }

    // Load user info for each unique user ID (batch if API supports it, or individually)
    // For now, we'll try to load them individually with a best-effort approach
    const userIdArray = Array.from(userIds);
    await Promise.all(
      userIdArray.slice(0, 10).map(async (userId) => {
        try {
          const response = await apiClient.get<{ data: User }>(`/users/${userId}`);
          if (response.data?.data?.displayName) {
            this.userNameCache.set(userId, response.data.data.displayName);
          }
        } catch {
          // Silently ignore - we'll just show the ID
        }
      })
    );
  }

  private attachAuditLogTableListeners(): void {
    // Pagination buttons
    const prevBtn = this.container.querySelector('#audit-prev-page');
    const nextBtn = this.container.querySelector('#audit-next-page');

    prevBtn?.addEventListener('click', () => {
      if (this.auditLogPagination.page > 1) {
        this.auditLogPagination.page--;
        this.loadAuditLog();
      }
    });

    nextBtn?.addEventListener('click', () => {
      if (this.auditLogPagination.hasMore) {
        this.auditLogPagination.page++;
        this.loadAuditLog();
      }
    });

    // Expand/collapse row buttons
    this.container.querySelectorAll('.expand-audit-row-btn').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.stopPropagation();
        const entryId = btn.getAttribute('data-entry-id');
        if (entryId) {
          this.toggleAuditLogRowExpansion(entryId);
        }
      });
    });

    // Row click to expand/collapse
    this.container.querySelectorAll('.audit-row').forEach((row) => {
      row.addEventListener('click', () => {
        const entryId = row.getAttribute('data-entry-id');
        if (entryId) {
          this.toggleAuditLogRowExpansion(entryId);
        }
      });
    });

    // Copy JSON buttons
    this.container.querySelectorAll('.copy-audit-json-btn').forEach((btn) => {
      btn.addEventListener('click', async (e) => {
        e.stopPropagation();
        const json = btn.getAttribute('data-json');
        if (json) {
          try {
            await navigator.clipboard.writeText(JSON.stringify(JSON.parse(json), null, 2));
            // Show brief feedback
            const originalHtml = btn.innerHTML;
            btn.innerHTML = `${renderIcon('check', { size: 'xs' }).outerHTML}<span class="ml-1">Copied!</span>`;
            setTimeout(() => {
              btn.innerHTML = originalHtml;
            }, 1500);
          } catch {
            // Fallback for older browsers
            const textArea = document.createElement('textarea');
            textArea.value = JSON.stringify(JSON.parse(json), null, 2);
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
          }
        }
      });
    });
  }

  private toggleAuditLogRowExpansion(entryId: string): void {
    if (this.auditLogExpandedRows.has(entryId)) {
      this.auditLogExpandedRows.delete(entryId);
    } else {
      this.auditLogExpandedRows.add(entryId);
    }

    // Re-render the table container only
    const container = this.container.querySelector('#audit-log-container');
    if (container) {
      container.innerHTML = this.renderAuditLogTable();
      this.attachAuditLogTableListeners();
    }
  }

  // ============================================================================
  // Utilities
  // ============================================================================

  private escapeHtml(str: string): string {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }
}

/**
 * Create admin settings view factory function
 */
export function createAdminSettingsView(container: HTMLElement): AdminSettingsView {
  return new AdminSettingsView(container);
}

/**
 * Admin settings view route handler
 */
export function adminSettingsView(): HTMLElement {
  const content = document.createElement('div');
  const view = new AdminSettingsView(content);
  view.init().catch((error) => {
    console.error('Failed to initialize admin settings view:', error);
    content.innerHTML = `
      <div class="flex items-center justify-center p-4 min-h-[400px]">
        <div class="glass-card w-full max-w-md p-8 rounded-2xl text-center">
          <h1 class="text-xl font-bold text-foreground mb-2">Error</h1>
          <p class="text-muted-foreground mb-4">Failed to load admin settings. Please try again later.</p>
        </div>
      </div>
    `;
  });

  // Wrap with sidebar layout
  return createSidebarLayout(content, {
    currentPath: '/admin/settings',
    title: 'Admin Settings',
  });
}

export default AdminSettingsView;
